import { z } from 'zod';

import type { VoucherView } from './traveler_view';
import { isCancellationRequestedVoucher, isDeadVoucherStatus } from './voucher_liveness';

/**
 * Traveler-facing fulfillment of a trip: "is what was booked guaranteed?". A
 * second axis next to `TripStatusSchema` (WHEN the trip is) — a trip with a
 * cancellation requested is still UPCOMING or ONGOING, so this state can never
 * live in the lifecycle enum. Not 1:1 with any voucher status: it is a rollup
 * over the trip's live vouchers, and the rollup may grow inputs without the
 * voucher vocabulary changing.
 *
 *  - `ISSUED`                 every live service is issued, none with a cancellation requested
 *  - `AWAITING_APPROVAL`      some live service waits on an approver's decision
 *  - `CANCELLATION_REQUESTED` every live service is issued AND has a cancellation requested
 *  - `PENDING`                anything else: being arranged, unknown status, or a
 *                             cancellation requested on only part of the trip
 */
export const TripFulfillmentSchema = z.enum([
  'ISSUED',
  'PENDING',
  'AWAITING_APPROVAL',
  'CANCELLATION_REQUESTED',
]);
export type TripFulfillment = z.infer<typeof TripFulfillmentSchema>;

type FulfillmentVoucher = Pick<VoucherView, 'status' | 'cancellationRequestedAt'>;

/**
 * Pure rollup of {@link TripFulfillment} from the trip's vouchers — the ONE rule
 * trip-api stamps on the wire and every surface reads. Dead vouchers
 * (`isDeadVoucherStatus`) promise nothing and are left out. `null` when there is
 * nothing to promise: no live service, or no live service with a declared status
 * (the lifecycle answers then, not a guess).
 *
 * An unknown status (`null`) among declared ones never lets the trip read as
 * ISSUED: the customer ACL returns `null` on purpose for a state it does not
 * recognize, and the rollup must not guess what the source refused to.
 */
export function deriveTripFulfillment(
  vouchers: readonly FulfillmentVoucher[],
): TripFulfillment | null {
  const live = vouchers.filter((voucher) => !isDeadVoucherStatus(voucher.status));
  if (!live.some((voucher) => voucher.status != null)) return null;
  if (live.some((voucher) => voucher.status === 'AWAITING_APPROVAL')) return 'AWAITING_APPROVAL';
  if (live.every(isCancellationRequestedVoucher)) return 'CANCELLATION_REQUESTED';
  const issued = live.every(
    (voucher) => voucher.status === 'CONFIRMED' && !isCancellationRequestedVoucher(voucher),
  );
  return issued ? 'ISSUED' : 'PENDING';
}
